import { type FunctionComponent, useEffect, useState, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import { colors } from '@retailer-platform/shared-components'
import { typography, Button } from '@retailer-platform/shared-components/src/tds'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import { useNotificationFeed, NotificationFilterType } from '../../hooks/useNotificationFeed'
import { useDomainMessages } from '../../utils/domain/intl'
import { useMarkNotificationsAsRead } from '../../hooks/useMarkNotificationsAsRead'
import { NotificationItem } from './NotificationItem'
import { type NotificationProps } from './NotificationItem'

export interface Account {
  id: string
  givenName: string
  surname: string
  email: string
}

const SectionTitle = styled.h2({
  display: 'flex',
  alignItems: 'center',
  margin: 0,
  ...typography.subtitle,
})

const TitleContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px 12px',
})

const EmptyStateMessage = styled.div({
  padding: '24px 24px',
  textAlign: 'center',
  ...typography.bodyRegular,
  color: colors.GRAYSCALE.X50,
})

interface NotificationFeedProps {
  filter: NotificationFilterType
  filterLabel: string
  onReachEnd?: () => void
  pollingInterval?: number
  isOpen?: boolean
  account: Account
}

interface AllNotificationFeedProps {
  onReachEnd?: () => void
  pollingInterval?: number
  account: Account
}

interface SingleNotificationFeedProps {
  filter: NotificationFilterType
  filterLabel: string
  onReachEnd?: () => void
  pollingInterval?: number
  account: Account
}

// Component for the "All" filter with multiple sections
const AllNotificationFeed: FunctionComponent<AllNotificationFeedProps> = ({
  onReachEnd,
  pollingInterval,
  account,
}) => {
  const [showEarlierFeed, setShowEarlierFeed] = useState(false)

  const i18n = useDomainMessages({
    loading: 'notificationsDomain.drawer.feed.loading',
    urgentFeed: 'notificationsDomain.drawer.feed.urgent',
    todayFeed: 'notificationsDomain.drawer.feed.today',
    earlierFeed: 'notificationsDomain.drawer.feed.earlier',
    noNotifications: 'notificationsDomain.drawer.noNotifications',
  })

  // Use the main hook with different filter types for each section
  const urgentFeed = useNotificationFeed({
    filterType: NotificationFilterType.Urgent,
    pollingInterval: pollingInterval || 0,
    account,
    limitInitialView: true,
  })

  const todayInfoFeed = useNotificationFeed({
    filterType: NotificationFilterType.TodayInfo,
    pollingInterval: pollingInterval || 0,
    account,
  })

  const earlierInfoFeed = useNotificationFeed({
    filterType: NotificationFilterType.EarlierInfo,
    pollingInterval: showEarlierFeed ? pollingInterval || 0 : 0,
    account,
  })

  const handleTodayFeedReachEnd = () => {
    setShowEarlierFeed(true)
  }

  const urgentNotifications = urgentFeed.notifications
  const todayNotifications = todayInfoFeed.notifications
  const earlierNotifications = earlierInfoFeed.notifications

  const isLoading =
    urgentFeed.loading || todayInfoFeed.loading || (showEarlierFeed && earlierInfoFeed.loading)
  const hasAnyNotifications =
    urgentNotifications.length > 0 ||
    todayNotifications.length > 0 ||
    earlierNotifications.length > 0

  if (isLoading && !hasAnyNotifications) {
    return <div css={{ padding: '24px 24px', textAlign: 'center' }}>{i18n.loading}</div>
  }

  if (!hasAnyNotifications && !isLoading) {
    onReachEnd?.()
    return <EmptyStateMessage>{i18n.noNotifications}</EmptyStateMessage>
  }

  return (
    <>
      <FeedSection
        title={i18n.urgentFeed}
        notifications={urgentNotifications}
        onNotificationCleared={urgentFeed.onNotificationCleared}
        loading={urgentFeed.loading}
        hasMore={urgentFeed.hasMore}
        onLoadMore={urgentFeed.loadMore}
        refetch={urgentFeed.refetch}
        data-testid="urgent-feed"
        limitInitialView
        hideLoadingState
        feedSection="urgent"
        account={account}
      />
      <FeedSection
        title={i18n.todayFeed}
        notifications={todayNotifications}
        onNotificationCleared={todayInfoFeed.onNotificationCleared}
        loading={todayInfoFeed.loading}
        hasMore={todayInfoFeed.hasMore}
        onLoadMore={todayInfoFeed.loadMore}
        refetch={todayInfoFeed.refetch}
        onReachEnd={handleTodayFeedReachEnd}
        data-testid="today-feed"
        hideLoadingState
        feedSection="today"
        account={account}
      />
      {showEarlierFeed && (
        <FeedSection
          title={i18n.earlierFeed}
          notifications={earlierNotifications}
          onNotificationCleared={earlierInfoFeed.onNotificationCleared}
          loading={earlierInfoFeed.loading}
          hasMore={earlierInfoFeed.hasMore}
          onLoadMore={earlierInfoFeed.loadMore}
          refetch={earlierInfoFeed.refetch}
          data-testid="earlier-feed"
          hideLoadingState
          feedSection="earlier"
          account={account}
        />
      )}
    </>
  )
}

// Component for single filter types
const SingleNotificationFeed: FunctionComponent<SingleNotificationFeedProps> = ({
  filter,
  filterLabel,
  onReachEnd,
  pollingInterval,
  account,
}) => {
  const i18n = useDomainMessages({
    noNotifications: 'notificationsDomain.drawer.noNotifications',
  })

  // Use the single hook with the filter parameter
  const feed = useNotificationFeed({
    filterType: filter,
    pollingInterval: pollingInterval || 0,
    account,
  })

  const filteredNotifications = feed.notifications

  if (filteredNotifications.length === 0 && !feed.loading && !feed.hasMore) {
    onReachEnd?.()
    return <EmptyStateMessage>{i18n.noNotifications}</EmptyStateMessage>
  }

  return (
    <FeedSection
      title={filterLabel}
      notifications={filteredNotifications}
      onNotificationCleared={feed.onNotificationCleared}
      hasMore={feed.hasMore}
      loading={feed.loading}
      onLoadMore={feed.loadMore}
      refetch={feed.refetch}
      data-testid={`${filter}-feed`}
      feedSection={filter}
      account={account}
    />
  )
}

// Main component that routes between All and Single feeds
export const NotificationFeed: FunctionComponent<NotificationFeedProps> = ({
  filter,
  filterLabel,
  onReachEnd,
  pollingInterval,
  account,
}) => {
  // Reset scroll position when filter changes
  useEffect(() => {
    const scrollContainer = document.querySelector('.mantine-Drawer-content')
    if (scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [filter])

  if (filter === NotificationFilterType.All) {
    return (
      <AllNotificationFeed
        onReachEnd={onReachEnd}
        pollingInterval={pollingInterval}
        account={account}
      />
    )
  }

  return (
    <SingleNotificationFeed
      filter={filter}
      filterLabel={filterLabel}
      onReachEnd={onReachEnd}
      pollingInterval={pollingInterval}
      account={account}
    />
  )
}

interface FeedSectionProps {
  title?: string
  notifications: NotificationProps[]
  onNotificationCleared: (id: string) => void
  limitInitialView?: boolean
  hasMore?: boolean
  loading?: boolean
  onLoadMore?: () => void
  onReachEnd?: () => void
  'data-testid'?: string
  hideLoadingState?: boolean
  emptyMessage?: string
  refetch?: () => void
  feedSection: string
  account: Account
}

const FeedSection: FunctionComponent<FeedSectionProps> = ({
  title,
  notifications,
  onNotificationCleared,
  limitInitialView = false,
  hasMore,
  loading,
  onLoadMore,
  onReachEnd,
  'data-testid': testId,
  hideLoadingState,
  emptyMessage,
  refetch,
  feedSection,
  account,
}) => {
  const i18n = useDomainMessages({
    markAllAsRead: 'notificationsDomain.drawer.feed.markAllAsRead',
    viewMore: 'notificationsDomain.drawer.feed.viewMore',
    loading: 'notificationsDomain.drawer.feed.loading',
  })

  const { markAsRead } = useMarkNotificationsAsRead()

  const [showingAll, setShowingAll] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const visibleNotifications = useMemo(() => {
    if (!limitInitialView || showingAll) {
      return notifications
    }
    return notifications.slice(0, 3)
    setShowingAll(false)
  }, [notifications, limitInitialView, showingAll])

  const handleMarkAllAsRead = async () => {
    const unreadNotificationIds = visibleNotifications
      .filter(notification => !notification.isRead)
      .map(notification => notification.id)

    if (unreadNotificationIds.length > 0) {
      trackEvent({
        id: 'notifications.drawer.feed.mark_all_read',
        description: 'User marked all notifications as read in a feed section',
        data: {
          feedSection,
          notificationCount: unreadNotificationIds.length,
          accountId: account.id,
          timestamp: new Date().toISOString(),
        },
      })
      await markAsRead(unreadNotificationIds)
      await refetch?.() // This will refresh the current set of notifications
    }
  }

  const checkAndLoadMore = useCallback(async () => {
    const scrollContainer = document.querySelector('.mantine-Drawer-content')
    if (!scrollContainer) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer
    const isAtBottom = scrollHeight - scrollTop - clientHeight <= 1000 // Once we're 1000px (~10 notifications) from the bottom, load more
    const needsMoreContent = scrollHeight <= clientHeight

    if ((isAtBottom || needsMoreContent) && hasMore && !loading && !isLoadingMore) {
      setIsLoadingMore(true)
      trackEvent({
        id: 'notifications.drawer.feed.load_more',
        description: 'User triggered load more notifications',
        data: {
          feedSection,
          currentCount: notifications.length,
          triggerMethod: 'scroll',
          accountId: account.id,
          timestamp: new Date().toISOString(),
        },
      })
      // Execute loadMore and reset loading state to prevent rapid concurrent calls
      try {
        await onLoadMore?.()
      } finally {
        setIsLoadingMore(false)
      }
    } else if ((isAtBottom || needsMoreContent) && !hasMore && !loading && onReachEnd) {
      onReachEnd()
    }
  }, [
    hasMore,
    loading,
    isLoadingMore,
    feedSection,
    notifications.length,
    account.id,
    onLoadMore,
    onReachEnd,
  ])

  useEffect(() => {
    if (limitInitialView && !showingAll) return

    // Check immediately after mount and when notifications change
    checkAndLoadMore()

    const handleScroll = () => {
      checkAndLoadMore()
    }

    const scrollContainer = document.querySelector('.mantine-Drawer-content')
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [limitInitialView, showingAll, checkAndLoadMore])

  // Update loading state when notifications change
  useEffect(() => {
    if (!loading) {
      setIsLoadingMore(false)
    }
  }, [loading])

  // Only show loading state if we're loading and have no notifications yet
  if (!hideLoadingState && loading && notifications.length === 0 && !hasMore) {
    return <div css={{ padding: '24px 24px', textAlign: 'center' }}>{i18n.loading}</div>
  }

  // If we have no notifications and we're not loading more, show empty state or nothing
  if (notifications.length === 0 && !loading && !hasMore) {
    onReachEnd?.()
    return emptyMessage ? (
      <div css={{ padding: '24px 24px', textAlign: 'center', color: colors.GRAYSCALE.X50 }}>
        {emptyMessage}
      </div>
    ) : null
  }

  const handleViewMore = async () => {
    trackEvent({
      id: 'notifications.drawer.feed.view_more',
      description: 'User clicked view more button',
      data: {
        feedSection,
        currentCount: visibleNotifications.length,
        accountId: account.id,
        timestamp: new Date().toISOString(),
      },
    })
    setIsLoadingMore(true)
    if (hasMore) {
      await onLoadMore?.()
    }

    setShowingAll(true)
    setIsLoadingMore(false)
  }

  return (
    <>
      {title && notifications.length > 0 && (
        <TitleContainer>
          <SectionTitle>{title}</SectionTitle>
          {notifications.some(n => !n.isRead) && (
            <Button variant="tertiary" onClick={handleMarkAllAsRead}>
              {i18n.markAllAsRead}
            </Button>
          )}
        </TitleContainer>
      )}
      <div data-testid={testId} css={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {visibleNotifications.map(notification => (
          <NotificationItem
            key={notification.id}
            {...notification}
            createdAt={notification.createdAt}
            onCleared={() => onNotificationCleared(notification.id)}
            refetch={refetch}
            feedSection={feedSection}
            accountId={account.id}
          />
        ))}
        {limitInitialView && (hasMore || !showingAll) && notifications.length > 0 && (
          <Button
            variant="tertiary"
            fullWidth
            onClick={handleViewMore}
            disabled={isLoadingMore}
            css={{
              borderBottom: `1px solid ${colors.GRAYSCALE.X10}`,
              borderRadius: 0,
              padding: '12px',
            }}
          >
            {isLoadingMore ? i18n.loading : i18n.viewMore}
          </Button>
        )}
        {showingAll && hasMore && !hideLoadingState && (
          <div css={{ padding: '12px 24px', textAlign: 'center', color: colors.GRAYSCALE.X50 }}>
            {loading ? i18n.loading : null}
          </div>
        )}
      </div>
    </>
  )
}
